import * as rxjs from 'rxjs';

import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Mutations from './Mutations';
import * as Relations from './Relations';
import * as Streams from './Streams';
import Dependencies from './Dependencies';
import * as Images from './Images';
import * as Values from './Values';
import { Objects, Properties, Stores } from '.';

const __moduleName__ = 'omniglot-data-live.Proposals';
export let debugMode = false;

export abstract class BaseMutator<
    MutationType extends Mutations.BaseMutation,
    StreamType extends Streams.Pullable & Streams.Watchable<MutationType>,
    ImageType
> {
    persistKey: Stores.Key<any>;
    peerId: string;
    mutations: MutationType[];
    updates: rxjs.Subject<MutationType>;
    lastParentMutation: undefined|MutationType;
    parentImage: ImageType;
    parent?: StreamType;
    subscriptions = new Streams.Subscriptions();

    constructor(
        peerId: string,
        persistKey: Stores.Key<any>,
        parentImage: ImageType
    ) {
        this.peerId = peerId;
        this.persistKey = persistKey;
        this.mutations = [];
        this.updates = Dependencies.makeSubject();
        this.parentImage = parentImage;
    }

    watchHasChanges(): Streams.ScalarStream<boolean, Elevated.Domain<boolean>> {
        const mutator = this;
        return new class implements Streams.ScalarStream<boolean, Elevated.Domain<boolean>> {
            persistKey = mutator.persistKey;
            peerId: string = Elevated.factories.makeObjectId();
            domain = Values.TheBooleanDomain;
            watch(peerId: string) { 
                return new class {
                    mutations(observer: rxjs.Observer<Mutations.OfScalar.Mutation<boolean>>) { 
                        let _mutation: null|Mutations.OfScalar.Mutation<boolean> = null;
                        let _value: undefined|boolean = mutator.mutations.length > 0;
                        _mutation = Mutations.OfScalar.Mutation.fromAssign(_mutation, _value);
                        observer.next(_mutation);
                        const subscriptions = new Streams.Subscriptions();
                        if(debugMode) console.log(`${mutator.peerId} : now watched by ${peerId}`);
                        subscriptions.add(mutator.updates.subscribe({
                            next: mutation => {
                                const value_ = mutator.mutations.length > 0;
                                if(value_ === _value) {
                                    // skip, no change to last reported value
                                } else {
                                    _mutation = Mutations.OfScalar.Mutation.fromAssign<boolean>(_mutation, value_)
                                    observer.next(_mutation);
                                }
                            },
                            complete: () => observer.complete(),
                            error: (error: any) => {
                                console.error(`subscription to ${mutator.persistKey} receives error ${error}`)
                            }
                        }));
                        return subscriptions;
                    }
                }
            }
            stateful() { return undefined }
            persistent() { return undefined }        
        }();
    }

    pull() {
        if(!this.parent?.pull) return Promise.resolve();
        return this.parent.pull();
    }

    watch(peerId: string) {
        const mutator = this;
        return new class {
            mutations(observer: rxjs.Observer<MutationType>): Streams.Subscriptions {
                const resetMutation = mutator._reset();
                if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) is now watched by ${peerId}`);
                if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) has ${resetMutation} reset`);
                if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) has ${mutator.mutations.length} mutations`);
                observer.next(resetMutation);
                const replayQueue = [... mutator.mutations];
                const sub = mutator.updates.subscribe({ next: (mutation) => {
                    if(replayQueue.length === 0) {
                        if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) downstream mutation forwarded ${mutation}`);
                        observer.next(mutation) 
                    } else {
                        if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) downstream mutation queued ${mutation}`);
                        replayQueue.push(mutation);
                    }
                }});
                while(replayQueue.length > 0) {
                    const mutation = replayQueue.shift();
                    if(mutation) {
                        if(debugMode) console.log(`${mutator.peerId} : (Proposals.BaseMutator) queued mutation delivered ${mutation}`);
                        observer.next(mutation);
                    }
                }

                const subscriptions = new Streams.Subscriptions();
                if(mutator.peerId === peerId) console.error(`Self-watching!`);
                subscriptions.add(sub);
                return subscriptions;
            }
        }
    }

    _lastMutation() {
        if(this.mutations.length > 0) {
            return Elevated.Reference.from(this.mutations[this.mutations.length - 1]);
        } else if(this.lastParentMutation !== undefined) {
            return Elevated.Reference.from(this.lastParentMutation);
        } else {
            return Elevated.Reference.fromNull<MutationType>();
        }
    }

    _replay(observer: rxjs.Observer<MutationType>) {
        observer.next(this._reset());
        for(let mutation of this.mutations) {
            observer.next(mutation);
        }
    }

    _push(...mutations_: MutationType[]) {
        const master = this.peerId;
        if(debugMode) console.log(`${this.peerId} : BaseMutator PUSH ${mutations_.map(mutation => mutation.toString())}`);
        mutations_.forEach(mutation_ => {
            this.mutations.push(mutation_);
            if(debugMode) console.log(`${this.peerId} : BaseMutator notifying ${this.updates.observers.length} observers of ${mutation_}`);
            this.updates.next(mutation_);    
        });
    }

    abstract _receive(received: MutationType): void;

    _parent(stream: StreamType, updates: rxjs.Subject<MutationType>) {
        this.parent = stream;
        const mutator = this;

        if(debugMode) console.log(`${stream.peerId} : now watched by child ${this.peerId}`);
        this.subscriptions.add(updates.subscribe({
            next: (received: MutationType) => {
                if(debugMode) console.log(`${this.peerId}: ${this.peerId} receives ${received} from parent ${stream.peerId}`); 
                if(debugMode) console.log(`${this.peerId}: ${this.peerId} notifying ${this.updates.observers.length}`); 

                mutator._receive(received);
                if(this.mutations.length === 0) {
                    this.updates.next(received);
                } else {
                    const _mutations = [ ...this.mutations];
                    if(debugMode) console.log(`${this.peerId}: Rebase begins with mutations = ${_mutations.map(item => item.toString())}`);
                    this.mutations = this.mutations.filter(_mutation => _mutation.getGlobalId() !== received.getGlobalId());
                    if(debugMode) console.log(`${this.peerId}: Rebase ends with mutations = ${this.mutations.map(item => item.toString())}`);
                    this._replay(this.updates);
                }
            }, 
            error: (error: any) => {
                console.error(`${this.peerId}: ${this.peerId} subscription to ${stream.peerId} receives error ${error}`)
            }, 
            complete: () => {
                console.log(`${this.peerId}: ${this.peerId} subscription to ${stream.peerId} is complete`)
            }
        }));
    }

    abstract _reset(): MutationType;

    _revert() {
        this.mutations = [];
        this.updates.next(this._reset());
        return Promise.resolve();
    }

    _commit() {
        const parentMutable = this.parent?.persistent();
        if(!parentMutable) {
            console.warn(`Cannot commit, Proposals.BaseProposal(${this.persistKey}) has parent stream ${this.parent?.peerId} with no persistent interface`);
            return Promise.resolve(this.mutations[this.mutations.length - 1]);
        }
        if(this.mutations.length === 0) return Promise.resolve();
        return parentMutable.save(this.peerId, ...this.mutations);
    }
}

export abstract class PropertyMutator<
    DomainType extends Elevated.Domain<any>,
    MutationType extends Mutations.BaseMutation,
    StreamType extends Streams.Pullable & Streams.Watchable<MutationType>,
    ImageType
> extends BaseMutator<
    MutationType,
    StreamType,
    ImageType
> {
    domain: DomainType;
    constructor(
        peerId: string,
        persistKey: Stores.Key<any>,
        domain: DomainType,
        parentImage: ImageType
    ) {
        super(peerId, persistKey, parentImage);
        this.domain = domain;
    }
}

export class ScalarMutator<
    PropertyTypeParams extends Properties.TypeParams
> extends PropertyMutator<
    PropertyTypeParams["Domain"],
    Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]>, 
    Streams.ScalarStream<PropertyTypeParams>,
    Images.ScalarImage<PropertyTypeParams["Value"]>
> implements Streams.ScalarStream<PropertyTypeParams> {
    constructor(
        peerId: string,
        context: Properties.Instance<PropertyTypeParams>,
        parent?: Streams.ScalarStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]>
    ) {
        super(peerId, Stores.Key.fromProperty(context.descriptor, context.binding), context.descriptor.getDomain(), new Images.ScalarImage());

        if(!parent) {
            if(debugMode) console.log(`${this.peerId}: ${this.persistKey} has no parent`);
        } else { 
            const parentUpdates = Dependencies.makeSubject();
            this._parent(parent, parentUpdates); 
            parent.watch(this.peerId).mutations(parentUpdates);
        }
    }    

    _reset(): Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]> {
        if(!this.parentImage) {
            return Mutations.OfScalar.Mutation.fromClear(null);
        } else {
            return this.parentImage.prime(Elevated.Reference.from(this.lastParentMutation || null));
        }
    }

    _receive(received: Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]>): void {
        this.parentImage && this.parentImage.update(received);
        if(!received.isPrime)
            this.lastParentMutation = received;        
    }

    stateful() {
        const mutator = this;
        return new class extends Streams.Stateful<Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]>> {
            getChanges() { return [ ... mutator.mutations ] }
            revert() { return mutator._revert() }
            commit() { return mutator._commit() }
            assign(value_: PropertyTypeParams["Value"]) {
                mutator._push(Mutations.OfScalar.Mutation.fromAssign(mutator._lastMutation(), value_));
                return this;
            }
            clear() {
                mutator._push(Mutations.OfScalar.Mutation.fromClear(mutator._lastMutation()));
                return this;
            }    
        }();
    }

    persistent() {
        const mutator = this;
        return new class implements Streams.Persistent<Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]>> {
            save(peerId: string, ...mutations: Mutations.OfScalar.Mutation<PropertyTypeParams["Value"]>[]) {
                mutator._push(...mutations);
                return Promise.resolve();
            }
        }();
    }
}

export class SetMutator<
    PropertyTypeParams extends Properties.TypeParams
> extends PropertyMutator<
    PropertyTypeParams["Domain"], 
    Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>, 
    Streams.SetStream<PropertyTypeParams>,
    Images.SetImage<PropertyTypeParams["Value"]>
> implements Streams.SetStream<PropertyTypeParams> {
    constructor(
        peerId: string,
        context: Properties.Instance<PropertyTypeParams>,
        parent?: Streams.SetStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]>
    ) {
        super(peerId, Stores.Key.fromProperty(context.descriptor, context.binding), context.descriptor.getDomain(), new Images.SetImage());

        if(!parent) {
            if(debugMode) console.log(`${this.peerId}: ${this.persistKey} has no parent`);
        } else { 
            const parentUpdates = Dependencies.makeSubject();
            this._parent(parent, parentUpdates); 
            parent.watch(this.peerId).mutations(parentUpdates);
        }
    }    

    _reset() {
        if(!this.parentImage) {
            return Mutations.OfSet.Mutation.fromClear(null);
        } else {
            return this.parentImage.prime(Elevated.Reference.from(this.lastParentMutation || null));
        }
    }

    _receive(received: Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>): void {
        this.parentImage.update(received);
        if(!received.isPrime)
            this.lastParentMutation = received;        
    }

    stateful() {
        const mutator = this;
        return new class extends Streams.Stateful<Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>>{
            getChanges() { return [ ... mutator.mutations ] }
            push(...mutations: Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>[]) {
                mutator._push(... mutations);
            }
            revert() { return mutator._revert() }
            commit() { return mutator._commit() }
            assign(values_: Set<PropertyTypeParams["Value"]>) {
                mutator._push(Mutations.OfSet.Mutation.fromAssign(mutator._lastMutation(), ...Array.from(values_)));
                return this;
            }
            clear() {
                mutator._push(Mutations.OfSet.Mutation.fromClear(mutator._lastMutation()));
                return this;
            }
            insert(...values_: Array<PropertyTypeParams["Value"]>) {
                mutator._push(Mutations.OfSet.Mutation.fromInsert(mutator._lastMutation(), ...Array.from(values_)));
                return this;
            }
            remove(..._values: Array<PropertyTypeParams["Value"]>) {
                mutator._push(Mutations.OfSet.Mutation.fromRemove(mutator._lastMutation(), ...Array.from(_values)));
                return this;
            }        
        }();
    }

    persistent() {
        const mutator = this;
        return new class implements Streams.Persistent<Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>> {
            save(peerId: string, ...mutations: Mutations.OfSet.Mutation<PropertyTypeParams["Value"]>[]) {
                mutator._push(...mutations);
                return mutator._commit();
            }
        }();
    }
}

export class SequenceMutator<
    PropertyTypeParams extends Properties.TypeParams
> extends PropertyMutator<
    PropertyTypeParams["Domain"], 
    Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>, 
    Streams.SequenceStream<PropertyTypeParams>,
    Images.SequenceImage<PropertyTypeParams["Value"]>
> implements Streams.SequenceStream<PropertyTypeParams> {
    constructor(
        peerId: string,
        context: Properties.Instance<PropertyTypeParams>,
        parent?: Streams.SequenceStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]>
    ) {
        super(peerId, Stores.Key.fromProperty(context.descriptor, context.binding), context.descriptor.getDomain(), new Images.SequenceImage());

        if(!parent) {
            if(debugMode) console.log(`${this.peerId}: ${this.persistKey} has no parent`);
        } else { 
            const parentUpdates = Dependencies.makeSubject();
            this._parent(parent, parentUpdates); 
            parent.watch(this.peerId).mutations(parentUpdates);
        }
    }

    _reset() {
        if(!this.parentImage) {
            return Mutations.OfSequence.Mutation.fromClear(null);
        } else {
            return this.parentImage.prime(Elevated.Reference.from(this.lastParentMutation || null));
        }
    }

    _receive(received: Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>): void {
        this.parentImage.update(received);
        if(!received.isPrime)
            this.lastParentMutation = received;        
    }

    stateful() {
        const mutator = this;
        return new class extends Streams.Stateful<Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>>{
            getChanges() { return [ ... mutator.mutations ] }
            push(...mutations: Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>[]) {
                mutator._push(... mutations);
            }
            revert() { return mutator._revert() }
            commit() { return mutator._commit() }
            assign(values_: PropertyTypeParams["Value"][]) {
                mutator._push(Mutations.OfSequence.Mutation.fromAssign(mutator._lastMutation(), ...values_));
                return this;
            }
            clear() {
                mutator._push(Mutations.OfSequence.Mutation.fromClear(mutator._lastMutation()));
                return this;
            }
            remove(..._values: PropertyTypeParams["Value"][]) {
                mutator._push(Mutations.OfSequence.Mutation.fromRemove(mutator._lastMutation(), ..._values));
                return this;
            }
            append(after: PropertyTypeParams["Value"]|undefined, ... values_: PropertyTypeParams["Value"][]) {
                mutator._push(Mutations.OfSequence.Mutation.fromAppend(mutator._lastMutation(), after, ... values_));
                return this;
            }
            prepend(before: PropertyTypeParams["Value"]|undefined, ... values_: PropertyTypeParams["Value"][]) {
                mutator._push(Mutations.OfSequence.Mutation.fromPrepend(mutator._lastMutation(), before, ...values_));
                return this;
            }
        }();
    }

    persistent() {
        const mutator = this;
        return new class implements Streams.Persistent<Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>> {
            save(peerId: string, ...mutations: Mutations.OfSequence.Mutation<PropertyTypeParams["Value"]>[]) {
                mutator._push(...mutations);
                return mutator._commit();
            }
        }();
    }

}

export class MapMutator<
    KeyType, 
    PropertyTypeParams extends Properties.TypeParams
> extends PropertyMutator<
    PropertyTypeParams["Domain"], 
    Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>, 
    Streams.MapStream<KeyType, PropertyTypeParams>,
    Images.MapImage<KeyType, PropertyTypeParams["Value"]>
> implements Streams.MapStream<KeyType, PropertyTypeParams> {
    makeKey: () => KeyType;

    constructor(
        peerId: string,
        context: Properties.Instance<PropertyTypeParams>,
        makeKey: () => KeyType,
        parent?: Streams.MapStream<KeyType, PropertyTypeParams["Value"], PropertyTypeParams["Domain"]>
    ) {
        super(peerId, Stores.Key.fromProperty(context.descriptor, context.binding), context.descriptor.getDomain(), new Images.MapImage());
        this.makeKey = makeKey;

        if(!parent) {
            if(debugMode) console.log(`${this.peerId}: ${this.persistKey} has no parent`);
        } else { 
            const parentUpdates = Dependencies.makeSubject();
            this._parent(parent, parentUpdates); 
            parent.watch(this.peerId).mutations(parentUpdates);
        }
    }

    _reset() {
        if(!this.parentImage) {
            return Mutations.OfMap.Mutation.fromClear<KeyType, PropertyTypeParams["Value"]>(null);
        } else {
            return this.parentImage.prime(Elevated.Reference.from(this.lastParentMutation || null));
        }
    }

    _receive(received: Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>): void {
        this.parentImage.update(received);
        if(!received.isPrime)
            this.lastParentMutation = received;        
    }


    stateful() {
        const mutator = this;
        return new class extends Streams.Stateful<Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>>{
            getChanges() { return [ ... mutator.mutations ] }
            push(...mutations: Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>[]) {
                mutator._push(... mutations);
            }
            revert() { return mutator._revert() }
            commit() { return mutator._commit() }
            assign(... pairs_: Mutations.OfMap.Pair<KeyType, PropertyTypeParams["Value"]>[]) {
                mutator._push(Mutations.OfMap.Mutation.fromAssign(mutator._lastMutation(), ...pairs_));
                return this;
            }
            clear() {
                mutator._push(Mutations.OfMap.Mutation.fromClear(mutator._lastMutation()));
                return this;
            }
            remove(... _pairs: Mutations.OfMap.Pair<KeyType,PropertyTypeParams["Value"]>[]) {
                mutator._push(Mutations.OfMap.Mutation.fromRemove(mutator._lastMutation(), ..._pairs));
                return this;
            }
            insert(... pairs_: Mutations.OfMap.Pair<KeyType,PropertyTypeParams["Value"]>[]) {
                mutator._push(Mutations.OfMap.Mutation.fromInsert(mutator._lastMutation(), ...pairs_));
                return this;
            }
            replace(key: KeyType, ...values_: PropertyTypeParams["Value"][]) {
                mutator._push(Mutations.OfMap.Mutation.fromReplace(mutator._lastMutation(), key, ...values_));
                return this;
            }
        }();
    }

    persistent() {
        const mutator = this;
        return new class implements Streams.Persistent<Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>> {
            save(peerId: string, ...mutations: Mutations.OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>[]) {
                mutator._push(...mutations);
                return mutator._commit();
            }
        }();
    }

}

export class RelationMutator<
    RelationBindingType extends Objects.BindingType
> extends BaseMutator<
    Mutations.OfRelation.Mutation<RelationBindingType>, 
    Streams.RelationStream<RelationBindingType>,
    Images.RelationImage<RelationBindingType>
> implements Streams.RelationStream<RelationBindingType> {
    anchor: Relations.Anchor<RelationBindingType>;

    constructor(
        peerId: string,
        anchor: Relations.Anchor<RelationBindingType>,
        parent?: Streams.RelationStream<RelationBindingType>
    ) {
        super(peerId, Stores.Key.fromRelation(anchor.descriptor, anchor.binding), new Images.RelationImage());
        this.anchor = anchor;

        if(!parent) {
            if(debugMode) console.log(`${this.peerId}: ${this.persistKey} has no parent`);
        } else { 
            const parentUpdates = Dependencies.makeSubject();
            this._parent(parent, parentUpdates); 
            parent.watch(this.peerId).mutations(parentUpdates);
        }
    }

    _reset() {
        if(!this.parentImage) {
            return Mutations.OfRelation.Mutation.fromClear<RelationBindingType>(null);
        } else {
            return this.parentImage.prime(Elevated.Reference.from(this.lastParentMutation || null));
        }
    }

    _receive(received: Mutations.OfRelation.Mutation<RelationBindingType>): void {
        if(debugMode) console.log(`${this.peerId}: Proposals${this.persistKey} updates parent image with ${received}`)
        this.parentImage.update(received);
        if(!received.isPrime)
            this.lastParentMutation = received;        
    }

    stateful() {
        const mutator = this;
        return new class extends Streams.Stateful<Mutations.OfRelation.Mutation<RelationBindingType>>{
            getChanges() { return [ ... mutator.mutations ] }
            push(...mutations: Mutations.OfRelation.Mutation<RelationBindingType>[]) {
                mutator._push(... mutations);
            }
            revert() { return mutator._revert() }
            commit() { return mutator._commit() }
            assign(values_: RelationBindingType[]) {
                mutator._push(Mutations.OfRelation.Mutation.fromAssign(mutator._lastMutation(), ...values_));
                return this;
            }
            clear() {
                mutator._push(Mutations.OfRelation.Mutation.fromClear(mutator._lastMutation()))
                return this;
            }
            insert(...values_: RelationBindingType[]) {
                mutator._push(Mutations.OfRelation.Mutation.fromInsert(mutator._lastMutation(), ...values_));
                return this;;
            }
            remove(..._values: RelationBindingType[]) {
                mutator._push(Mutations.OfRelation.Mutation.fromRemove(mutator._lastMutation(), ..._values));
                return this;
            }        
        };
    }

    persistent() {
        const mutator = this;
        return new class implements Streams.Persistent<Mutations.OfRelation.Mutation<RelationBindingType>> {
            save(peerId: string, ...mutations: Mutations.OfRelation.Mutation<RelationBindingType>[]) {
                mutator._push(...mutations);
                return mutator._commit();
        }
        };
    }
}
